import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
    BrandsToBeAddedToOrganization,
    CurrentBrand,
    OrganizationData,
    TeamMember
} from 'modules/Types';
import moment from 'moment';
import { goToUrl } from 'modules/Utils';
import { addNewTeamMember_api, completeOrganizationSetup_api, getOrganization_api, getOrganizationTeamMembers_api, getSubscriptionStatusUrl_api, removeOrganizationMember_api, UpdatedTierBrandResponse, updateTierInOrganization_api } from 'api/organizationApi';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { OrganizationRole, PlanType, TEAM_PLAN_INCLUDED_PRO_ACCOUNTS } from 'modules/Const';
import useUser from './useUser';
import useBrand from './useBrand';
import { createOrganizationCheckout_api } from 'api/subscriptionApi';

interface OrganizationState {
    organization: OrganizationData | undefined;
    isOrganizationInitialized: boolean;
    organizationBrandIds: number[];
    selectedBrandId: number | undefined | null;
    selectedTeamMemberId: number | undefined | null;
    teamMembers: TeamMember[];

    isLoadingOrganization: boolean;
    isLoadingTeamMembers: boolean;
    isLoadingSubscriptionView: boolean;
    isLoadingTab: boolean;

    /** Actions */
    setOrganization: (organization: OrganizationData | undefined) => void;
    setOrganizationBrandIds: (brandIds: number[]) => void;
    setSelectedBrandId: (brandId: number | undefined | null) => void;
    getSelectedBrand: () => CurrentBrand | undefined;
    setSelectedTeamMemberId: (teamMemberId: number | undefined | null) => void;
    getSelectedTeamMember: () => TeamMember | undefined;
    setIsLoadingOrganization: (value: boolean) => void;
    setIsLoadingSubscriptionView: (value: boolean) => void;
    setTeamMembers: (members: TeamMember[]) => void;
    setIsLoadingTeamMembers: (value: boolean) => void;
    setIsLoadingTab: (value: boolean) => void;

    /** Computed values */
    proAccountsCount: () => number;
    isOrganizationReady: () => boolean;
    hasOrganizationAdminAccess: () => boolean;
    teamFreeTrialRedeemed: () => boolean;
    remainingFreeTrial: () => number;
    isOrganizationInactive: () => boolean;
    isPaymentFailed: () => boolean;
    hasProAccountOverage: () => boolean;
    brandIsInOrganization: (brand: CurrentBrand | null | undefined) => boolean;
    brandIdIsInOrganization: (brandId: number | null | undefined) => boolean;
    isSelectedTeamMemberMyself: () => boolean;
    getMyselfAsTeamMember: () => TeamMember | undefined;
    getOrganizationId: (organizationId?: number | undefined) => number;

    /** Methods */
    completeOrganizationSetup: (name: string, brands: BrandsToBeAddedToOrganization[]) => Promise<OrganizationData>;
    getOrganization: (organizationId?: number) => Promise<OrganizationData | null | undefined>;
    getOrganizationBrands: (organizationId?: number) => CurrentBrand[];
    getOrganizationTeamMembers: (organizationId?: number) => Promise<void>;
    addOrganizationTeamMember: (data: any, organizationId?: number) => Promise<void>;
    removeOrganizationTeamMember: (memberId: number, organizationId?: number) => Promise<void>;
    goToTeamCheckout: (data: { recurring_interval: BillingCycle; brandSlug?: string | null }, organizationId?: number) => Promise<void>;
    redirectToSubscriptionStatus: (organizationId?: number) => Promise<void>;
    updateMemberStatusInOrganization: (status: 'upgrade' | 'downgrade' | undefined, brandId: number, organizationId?: number) => Promise<void>;
}

export interface OganizationExistsState extends OrganizationState {
    organization: OrganizationData;
}

const useOrganization = create(
    immer<OrganizationState>((set, get) => ({
        /** Core state values */
        organization: undefined,
        isOrganizationInitialized: false,
        organizationBrandIds: [],
        selectedBrandId: undefined,
        selectedTeamMemberId: undefined,
        teamMembers: [],

        /** Loading state flags */
        isLoadingOrganization: false,
        isLoadingSubscriptionView: false,
        isLoadingTeamMembers: false,
        isLoadingTab: false,

        /** Basic state setters */
        setOrganization: (organization: OrganizationData | undefined) => set(state => { state.organization = organization }),
        setOrganizationBrandIds: (brandIds: number[]) => set(state => { state.organizationBrandIds = brandIds }),
        setSelectedBrandId: (brandId: number | undefined | null) => set(state => { state.selectedBrandId = brandId }),

        /** Gets the currently selected brand from the brands dictionary */
        getSelectedBrand: () => {
            const { loadedBrandsDict } = useBrand.getState();
            const { selectedBrandId } = get();
            return selectedBrandId ? loadedBrandsDict?.[selectedBrandId] : undefined;
        },

        /** Loading state setters */
        setSelectedTeamMemberId: (teamMemberId: number | undefined | null) => set(state => { state.selectedTeamMemberId = teamMemberId }),
        setIsLoadingOrganization: (value) => set(state => { state.isLoadingOrganization = value }),
        setIsLoadingSubscriptionView: (value) => set(state => { state.isLoadingSubscriptionView = value }),
        setTeamMembers: (members) => set(state => { state.teamMembers = members }),
        setIsLoadingTeamMembers: (value) => set(state => { state.isLoadingTeamMembers = value }),

        /** Gets the currently selected team member from the members array */
        getSelectedTeamMember: () => {
            const { teamMembers } = get();
            const { selectedTeamMemberId } = get();
            return selectedTeamMemberId ? teamMembers.find(member => member.id === selectedTeamMemberId) : undefined;
        },
        setIsLoadingTab: (value) => set(state => { state.isLoadingTab = value }),

        /** Returns count of Pro tier brands in the organization */
        proAccountsCount: () => {
            const { organizationBrandIds } = get();
            const { loadedBrandsDict } = useBrand.getState();
            return organizationBrandIds.filter(brandId => loadedBrandsDict?.[brandId]?.tier === PlanType.PRO).length || 0;
        },

        /** Checks if organization status is 'ready' */
        isOrganizationReady: () => {
            return get().organization?.status === 'ready';
        },

        /** Validates if current user has admin access to organization */
        hasOrganizationAdminAccess: () => {
            const { organization } = get();
            const { currentUser, isSymphonyAdmin } = useUser.getState();
            if (!organization) return false;
            if (isSymphonyAdmin) return true;
            if (organization.owner_id === currentUser?.id) return true;
            const { role } = organization;
            return role === OrganizationRole.ADMINISTRATOR || role === OrganizationRole.OWNER || role === OrganizationRole.MEMBER
        },

        /** Checks if team free trial has been redeemed */
        teamFreeTrialRedeemed: () => {
            const { currentUser } = useUser.getState();
            const organization = get().organization;
            return Boolean(
                !(organization?.role === 'administrator' || organization?.role === 'owner') &&
                currentUser?.metadata?.organization_subscription?.organization_free_trial_redeemed
            );
        },

        /** Calculates remaining days in free trial */
        remainingFreeTrial: () => {
            const { currentUser } = useUser.getState();
            const subscription = currentUser?.metadata?.organization_subscription;
            if (!subscription?.organization_free_trial_redeemed || !subscription?.trial_start_date) return 0;
            const now = moment();
            const expiresAt = moment(subscription.trial_start_date).add(14, 'days');
            return expiresAt.isBefore(now) ? 0 : expiresAt.diff(now, 'days');
        },

        /** Checks if organization subscription is inactive */
        isOrganizationInactive: () => {
            const { currentUser } = useUser.getState();
            const subscription = currentUser?.metadata?.organization_subscription;
            return Boolean(subscription && !['completed', 'succeeded', 'active'].includes(subscription.status!));
        },

        /** Checks if organization payment has failed */
        isPaymentFailed: () => {
            const { currentUser } = useUser.getState();
            return currentUser?.metadata?.organization_subscription?.status === 'failed';
        },

        /** Checks if organization has more Pro accounts than allowed */
        hasProAccountOverage: () => {
            return get().getOrganizationBrands()?.filter(brand => brand.tier === PlanType.PRO).length > TEAM_PLAN_INCLUDED_PRO_ACCOUNTS;
        },

        /** Validates if a brand belongs to the organization */
        brandIsInOrganization: (brand: CurrentBrand | null | undefined) => {
            if (!brand) return false;
            const { organization } = get();
            return Boolean(organization?.id && brand.organizationId === organization.id);
        },

        /** Validates if a brand ID belongs to the organization */
        brandIdIsInOrganization: (brandId: number | null | undefined) => {
            if (!brandId) {
                return false;
            }
            const { organization } = get();
            const { loadedBrandsDict } = useBrand.getState();
            const brand = loadedBrandsDict?.[brandId];
            return Boolean(organization?.id && brand?.organizationId === organization.id);
        },

        /** Checks if selected team member is the current user */
        isSelectedTeamMemberMyself: () => {
            const { currentUser } = useUser.getState();
            const { selectedTeamMemberId } = get();
            return currentUser?.id === selectedTeamMemberId;
        },

        /** Gets current user's team member record */
        getMyselfAsTeamMember: () => {
            const { currentUser, isSymphonyAdmin } = useUser.getState();
            const { teamMembers } = get();
            if (isSymphonyAdmin && currentUser) {
                return {
                    id: currentUser.id,
                    email: currentUser.email,
                    role: OrganizationRole.OWNER,
                    brands: [],
                    isCurrentUser: true,
                }
            }

            return teamMembers.find(member => member.id === currentUser?.id);
        },

        /** Gets organization ID, using provided ID or current organization */
        getOrganizationId: (organizationId?: number | undefined) => {
            const { organization } = get();
            return organizationId || organization?.id!;
        },

        /** Completes organization setup with name and brands */
        completeOrganizationSetup: async (name: string, brands: any) => {
            const { getOrganizationId } = get();
            const organizationId = getOrganizationId();
            const { getBrands } = useBrand.getState();
            set({ isLoadingOrganization: true });
            try {
                const res = await completeOrganizationSetup_api(organizationId, name, brands);
                const { organization: organizationLocal } = res;
                if (organizationLocal) {
                    set({
                        organization: organizationLocal,
                        organizationBrandIds: organizationLocal.brands.map((brandData: CurrentBrand) => brandData.id),
                    });
                }
                await getBrands(true);
                return organizationLocal;
            } catch (error) {
                console.error('Failed to create organization:', error);
                return null;
            } finally {
                set({ isLoadingOrganization: false });
            }
        },

        /** Fetches organization data and related information */
        getOrganization: async (organizationId?: number): Promise<OrganizationData | null | undefined> => {
            const { getOrganizationId, getOrganizationTeamMembers } = get();
            const { updateOrAddBrands } = useBrand.getState();
            const { isSymphonyAdmin } = useUser.getState();
            organizationId = getOrganizationId(organizationId);

            set({ isLoadingOrganization: true });
            try {
                const organizationLocal = await getOrganization_api(organizationId)
                const tempOrganizationId = organizationLocal?.id;

                if (organizationLocal?.brands && isSymphonyAdmin) {
                    updateOrAddBrands(organizationLocal?.brands || [])
                }

                if (tempOrganizationId) {
                    await getOrganizationTeamMembers(tempOrganizationId)
                }

                set({
                    organization: organizationLocal || undefined,
                    organizationBrandIds: organizationLocal?.brands.map((brandData: CurrentBrand) => brandData.id) || [],
                });

                return organizationLocal;
            } catch (error) {
                console.error('Failed to fetch organization data:', error);
            } finally {
                set({
                    isOrganizationInitialized: true,
                    isLoadingOrganization: false,
                });
            }
        },

        /** Gets all brands belonging to the organization */
        getOrganizationBrands: () => {
            const { loadedBrandsDict } = useBrand.getState();
            const { organizationBrandIds } = get();
            return organizationBrandIds.map(brandId => loadedBrandsDict?.[brandId]!) || [];
        },

        /** Fetches and updates team members list */
        getOrganizationTeamMembers: async (organizationId?: number) => {
            const { currentUser } = useUser.getState();
            const { getOrganizationId } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                set(state => { state.isLoadingTeamMembers = true });
                const members = await getOrganizationTeamMembers_api(organizationId);
                if (members) {
                    const membersWithCurrentUser = members.map((member: TeamMember) => ({
                        ...member,
                        isCurrentUser: member.id === currentUser?.id
                    }));
                    set(state => { state.teamMembers = membersWithCurrentUser });
                }
            } catch (error) {
                console.error('Failed to get team members:', error);
            } finally {
                set(state => { state.isLoadingTeamMembers = false });
            }
        },

        /** Adds new team member to organization */
        addOrganizationTeamMember: async (data: any, organizationId?: number) => {
            const { getOrganizationId } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                set(state => { state.isLoadingTeamMembers = true });
                await addNewTeamMember_api(organizationId, data)
                await get().getOrganizationTeamMembers()
            } catch (error) {
                console.error('Failed to add team member:', error);
            } finally {
                set(state => { state.isLoadingTeamMembers = false });
            }
        },

        /** Removes team member from organization */
        removeOrganizationTeamMember: async (memberId: number, organizationId?: number) => {
            const { getOrganizationId } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                set(state => { state.isLoadingTeamMembers = true });
                await removeOrganizationMember_api(organizationId, memberId)
                await get().getOrganizationTeamMembers()
            } catch (error) {
                console.error('Failed to remove team member:', error);
            } finally {
                set(state => { state.isLoadingTeamMembers = false });
            }
        },

        /** Initiates team checkout process */
        goToTeamCheckout: async (data: { recurring_interval: BillingCycle; brandSlug?: string | null }, organizationId?: number) => {
            const { getOrganizationId } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                if (!data.brandSlug) {
                    throw new Error('Brand slug is required');
                }
                const response = await createOrganizationCheckout_api(data.brandSlug, data.recurring_interval);
                window.location.href = response;
            } catch (error) {
                console.error('Organization checkout failed: ', error);
            }
        },

        /** Redirects to subscription status page */
        redirectToSubscriptionStatus: async (organizationId?: number) => {
            const { getOrganizationId, setIsLoadingSubscriptionView } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                setIsLoadingSubscriptionView(true);
                const url = await getSubscriptionStatusUrl_api(organizationId);
                goToUrl(url, setIsLoadingSubscriptionView);
            } catch (error) {
                setIsLoadingSubscriptionView(false);
                console.error('redirectToSubscriptionStatus: ', error);
            }
        },

        /** Updates member's tier status in organization */
        updateMemberStatusInOrganization: async (status: 'upgrade' | 'downgrade' | undefined, brandId: number, organizationId?: number) => {
            const { loadedBrandsDict, updateOrAddBrand } = useBrand.getState();
            if (!status) return;

            const { getOrganizationId } = get();
            organizationId = getOrganizationId(organizationId);

            try {
                set({ isLoadingTeamMembers: true });
                let updatedBrand = await updateTierInOrganization_api(organizationId, brandId, status)
                let tempBrand = { ...loadedBrandsDict?.[brandId] }
                tempBrand.tier = updatedBrand?.tier
                updateOrAddBrand(tempBrand as CurrentBrand)
            } catch (error) {
                console.error('Failed to update member status:', error);
            } finally {
                set({ isLoadingTeamMembers: false });
            }
        },
    }))
);

export default useOrganization;